import { useRef, useState } from "react";
import useUnmount from "react-use/lib/useUnmount";

import { Instruction, Name } from "../../../@types/visualization";

export type TooltipData = {
  instructions: Array<Instruction[]> | null;
  names: Name[] | null;
  position: { top: number | null; left: number | null };
  pages: { total: number; currentPage: number };
  tooltipKey: string | null;
};

const INITIAL_DATA: TooltipData = {
  instructions: null,
  names: null,
  position: {
    top: null,
    left: null
  },
  pages: {
    total: 1,
    currentPage: 1
  },
  tooltipKey: null
};

const TOP_OFFSET = 5;
const LEFT_OFFSET = 4;

export const useTooltip = () => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<TooltipData>(INITIAL_DATA);

  const handleClickOutsideTooltip = (e: MouseEvent) => {
    if (!e.target) return;
    const tooltip = tooltipRef.current;
    if (!tooltip?.contains(e.target as Node)) {
      setData(INITIAL_DATA);
    }
  };

  const close = () => {
    setData(INITIAL_DATA);
  };

  const changePage = (direction: string) => {
    setData(prevData => {
      const newData = { ...prevData };
      const pages = newData.pages;
      let pageNumber = pages.currentPage;
      if (direction === "next" && pages.currentPage !== pages.total) {
        pageNumber += 1;
      } else if (direction === "prev" && pages.currentPage !== 1) {
        pageNumber -= 1;
      }
      newData.pages.currentPage = pageNumber;
      return newData;
    });
  };

  const open = (
    instructions: Array<Instruction[]> | null,
    names: Name[],
    event: React.MouseEvent<Element>,
    container: HTMLDivElement | null
  ) => {
    if (!container) return;
    const containerPosition = container?.getBoundingClientRect();
    const newTooltipKey = event.currentTarget.getAttribute("data-key");

    setData(prevTooltipData => {
      const previousTooltipKey = prevTooltipData.tooltipKey;

      if (!previousTooltipKey) {
        document.addEventListener("mousedown", handleClickOutsideTooltip);
      }

      if (newTooltipKey === previousTooltipKey) {
        return prevTooltipData;
      }

      return {
        instructions,
        names,
        pages: {
          total: names.length,
          currentPage: 1
        },
        position: {
          top:
            event.pageY - containerPosition.top + TOP_OFFSET - window.scrollY,
          left:
            event.pageX - containerPosition.left + LEFT_OFFSET - window.scrollX
        },
        tooltipKey: newTooltipKey
      };
    });
  };

  useUnmount(() => () => {
    document.removeEventListener("mousedown", handleClickOutsideTooltip);
  });

  return {
    tooltipRef,
    data,
    open,
    close,
    changePage
  };
};
